/**
 * Layer Stack (CL-165, roadmap H3.2) — the pedagogical overlay-stacking
 * registry: NAMED, transparency-controlled, toggleable entries for every
 * rendered layer group.
 *
 * Why: Level-3 pedagogy (grades 6–8, map-as-model / overlay thinking) needs
 * lessons and students to manipulate the stack explicitly — "hide the
 * population layer", "make the rivers see-through", "turn volcanoes back
 * on". The compound engine (Phase 2) already RENDERS stacked overlays; this
 * module makes each rendered group addressable by name afterwards.
 *
 * How entries appear: the pipeline executor snapshots MapLibre + Deck layer
 * ids around every op and registers whatever the op added, named via
 * stackNameForStep(). No render method needs to know about the stack.
 *
 * Pure data + functions (no map access) — LayerManager applies the actual
 * visibility/opacity changes. Unit-tested in tests/layer-stack.test.js.
 */

/** Max named entries tracked (matches the spirit of globe-context's 5-layer cap). */
export const MAX_STACK_ENTRIES = 8

export function createStack() {
  return { entries: [] }
}

/**
 * Derive a student-friendly stack name from a pipeline step.
 * Examples: {op:'choropleth', metric:'population'} → 'population choropleth';
 * {op:'volcanoes'} → 'volcanoes'; {op:'h3_density', category:'schools'} →
 * 'schools density'.
 */
export function stackNameForStep(step) {
  if (!step || typeof step.op !== 'string') return null
  const op = step.op
  const pick = (...keys) => {
    for (const k of keys) {
      const v = step[k]
      // CL-174: normalize underscored ids ('major_cities' → 'major cities')
      // so stack names match how students speak.
      if (typeof v === 'string' && v.trim()) return v.trim().toLowerCase().replace(/_/g, ' ')
    }
    return null
  }
  const subject = pick(
    'metric',
    'indicator',
    'category',
    'poi_category',
    'dataset',
    'layer',
    'feature',
    'feature_type'
  )
  const base = op.replace(/_/g, ' ')
  if (op === 'choropleth' || op === 'admin_choropleth')
    return subject ? `${subject} choropleth` : 'choropleth'
  if (op === 'h3_density') return subject ? `${subject} density` : 'density'
  if (op === 'filter' || op === 'forced_poi') return subject ? `${subject} markers` : 'markers'
  // CL-170: students say "the coordinate grid", not "the graticule".
  if (op === 'graticule') return 'coordinate grid'
  // CL-174: physical-feature steps carry the real layer identity in
  // step.feature ('major_cities', 'geographic_lines'…) — name the stack row
  // by it, or every physical layer collides on 'physical feature' and
  // replaces the previous one's row (found live-verifying CL-173).
  if (op === 'physical_feature' || op === 'physical_features') {
    return subject || base
  }
  return subject && !base.includes(subject) ? `${subject} ${base}` : base
}

/**
 * Register (or replace) a named entry. Oldest entries are dropped past the
 * cap. Entries with no layer ids are ignored.
 *
 * CL-173 (H3.2b): pass `onEvict` to be told about entries dropped past the
 * cap — in stack mode the caller must remove their layers from the map, or
 * they'd linger rendered but uncontrollable.
 * @param {{entries: Array}} stack
 * @param {{name: string, op?: string, mapLayerIds?: string[], deckIds?: string[]}} entry
 * @param {(evicted: object) => void} [onEvict]
 */
export function registerEntry(stack, entry, onEvict) {
  if (!stack || !entry || typeof entry.name !== 'string' || !entry.name.trim()) return null
  const mapLayerIds = Array.isArray(entry.mapLayerIds) ? entry.mapLayerIds.filter(Boolean) : []
  const deckIds = Array.isArray(entry.deckIds) ? entry.deckIds.filter(Boolean) : []
  if (mapLayerIds.length === 0 && deckIds.length === 0) return null

  const name = entry.name.trim().toLowerCase()
  stack.entries = stack.entries.filter((e) => e.name !== name)
  const registered = {
    name,
    op: entry.op || null,
    mapLayerIds,
    deckIds,
    visible: true,
    opacity: null, // null = untouched (each layer's authored default)
    addedAt: Date.now(),
  }
  stack.entries.push(registered)
  if (stack.entries.length > MAX_STACK_ENTRIES) {
    const evicted = stack.entries.slice(0, stack.entries.length - MAX_STACK_ENTRIES)
    stack.entries = stack.entries.slice(-MAX_STACK_ENTRIES)
    if (typeof onEvict === 'function') {
      for (const e of evicted) {
        try {
          onEvict(e)
        } catch {
          /* eviction cleanup is best-effort */
        }
      }
    }
  }
  return registered
}

/**
 * Resolve a student's layer reference against the stack, most recent first.
 * Match order: exact name → name contains query (or vice versa) → op match.
 * @returns the entry or null
 */
export function resolveEntry(stack, query) {
  if (!stack || typeof query !== 'string' || !query.trim()) return null
  const q = query
    .trim()
    .toLowerCase()
    .replace(/^(the|a|my)\s+/, '') // "the population layer" → "population layer"
    .replace(/\s+layers?$/, '') //   "population layer"     → "population"
    .trim()
  const newestFirst = [...stack.entries].reverse()
  return (
    newestFirst.find((e) => e.name === q) ||
    newestFirst.find((e) => e.name.includes(q) || q.includes(e.name)) ||
    newestFirst.find((e) => e.op && (e.op === q || q.includes(e.op.replace(/_/g, ' ')))) ||
    null
  )
}

/** Remove one entry by resolved reference. Returns the removed entry or null. */
export function removeEntry(stack, entry) {
  if (!stack || !entry) return null
  const before = stack.entries.length
  stack.entries = stack.entries.filter((e) => e !== entry)
  return stack.entries.length < before ? entry : null
}

/** Names + visibility for "what layers are on the map?" (newest first). */
export function listEntries(stack) {
  return [...(stack?.entries || [])]
    .reverse()
    .map((e) => ({ name: e.name, visible: e.visible, opacity: e.opacity }))
}

export function clearEntries(stack) {
  if (stack) stack.entries = []
}
